/**
 * DateInput logic (ux/01 §3.3, AUD-UXDS-23) — pure, UTC-based, no Date parsing
 * of free text. The field is one text stop masked JJ/MM/AAAA; the value that
 * travels is an ISO date string « YYYY-MM-DD ».
 *
 * Accepted while typing / on blur: `27092026`, `270926`, `27/9/26`,
 * `27-09-2026`, `27.09.2026`, `27 09 2026` → « 27/09/2026 ». A two-digit year
 * is 20YY. An impossible date (31/02) is flagged, never rolled over.
 */

const pad2 = (n) => String(n).padStart(2, "0");

/** ISO « YYYY-MM-DD » → « JJ/MM/AAAA » (or "" for an empty / bad value). */
export function isoToDisplay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/** Days in a month (month 1-12), leap years included. */
export function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Today as ISO in the local calendar (what the pharmacist's clock says). */
export function todayIso(now = new Date()) {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

/**
 * Auto-insert the slashes while the user types digits (ux/01 §3.3). Only acts
 * when the text grew and holds digits with slashes at the mask positions, so
 * deleting a slash is never fought and a user who types his own separators
 * (« 27/9/26 ») keeps them.
 * @param {string} prev text before the keystroke
 * @param {string} next text after the keystroke
 */
export function autoMaskDate(prev, next) {
  const p = String(prev ?? "");
  const n = String(next ?? "");
  if (n.length <= p.length) return n;
  if (!/^[\d/]*$/.test(n)) return n;
  const slashAt = [...n].flatMap((c, i) => (c === "/" ? [i] : []));
  if (slashAt.some((i) => i !== 2 && i !== 5)) return n;
  const digits = n.replace(/\//g, "").slice(0, 8);
  if (digits.length <= 2) return digits.length === 2 && n.endsWith("/") ? `${digits}/` : digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Parse a typed date.
 * @param {string} text
 * @param {{ min?: string, max?: string }} [opts] ISO bounds
 * @returns {{ value: string|null, display: string, error: string|null }}
 */
export function parseDateInput(text, { min, max } = {}) {
  const raw = String(text ?? "").trim();
  if (raw === "") return { value: null, display: "", error: null };
  let d;
  let m;
  let y;
  let hit = /^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{2}|\d{4})$/.exec(raw);
  if (hit) {
    [, d, m, y] = hit;
  } else if ((hit = /^(\d{2})(\d{2})(\d{4}|\d{2})$/.exec(raw))) {
    [, d, m, y] = hit;
  } else {
    return { value: null, display: raw, error: "Date incomplète : JJ/MM/AAAA" };
  }
  const day = Number(d);
  const month = Number(m);
  const year = y.length === 2 ? 2000 + Number(y) : Number(y);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    return { value: null, display: raw, error: "Cette date n'existe pas" };
  }
  const value = `${year}-${pad2(month)}-${pad2(day)}`;
  const display = isoToDisplay(value);
  if (min && value < min) {
    return { value: null, display, error: `Date antérieure au ${isoToDisplay(min)}` };
  }
  if (max && value > max) {
    return { value: null, display, error: `Date postérieure au ${isoToDisplay(max)}` };
  }
  return { value, display, error: null };
}

// ─── Calendar popover (Alt+↓) ────────────────────────────────────────────────

const toUtc = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const fromUtc = (dt) =>
  `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;

/** Add months, clamping the day to the target month's length. */
export function addMonths(iso, n) {
  const [y, m, d] = iso.split("-").map(Number);
  const total = y * 12 + (m - 1) + n;
  const ty = Math.floor(total / 12);
  const tm = (total % 12) + 1;
  return `${ty}-${pad2(tm)}-${pad2(Math.min(d, daysInMonth(ty, tm)))}`;
}

export function addDays(iso, n) {
  const dt = toUtc(iso);
  dt.setUTCDate(dt.getUTCDate() + n);
  return fromUtc(dt);
}

/** 0 = lundi … 6 = dimanche (French weeks start on Monday). */
export function weekdayIndex(iso) {
  return (toUtc(iso).getUTCDay() + 6) % 7;
}

/**
 * The calendar's key map: arrows = day / week, PgUp/PgDn = month,
 * Début/Fin = start / end of the week. Returns the new active ISO date, or
 * null when the key is not a calendar move.
 */
export function moveCalendarDate(iso, key) {
  switch (key) {
    case "ArrowLeft":
      return addDays(iso, -1);
    case "ArrowRight":
      return addDays(iso, 1);
    case "ArrowUp":
      return addDays(iso, -7);
    case "ArrowDown":
      return addDays(iso, 7);
    case "PageUp":
      return addMonths(iso, -1);
    case "PageDown":
      return addMonths(iso, 1);
    case "Home":
      return addDays(iso, -weekdayIndex(iso));
    case "End":
      return addDays(iso, 6 - weekdayIndex(iso));
    default:
      return null;
  }
}

/**
 * Weeks of the month holding `iso`, Monday first; days outside the month are
 * null.
 * @returns {Array<Array<string|null>>}
 */
export function monthGrid(iso) {
  const [y, m] = iso.split("-").map(Number);
  const first = `${y}-${pad2(m)}-01`;
  const lead = weekdayIndex(first);
  const n = daysInMonth(y, m);
  const cells = [
    ...Array(lead).fill(null),
    ...Array.from({ length: n }, (_, i) => `${y}-${pad2(m)}-${pad2(i + 1)}`),
  ];
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export const MONTH_NAMES = Object.freeze([
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
]);
export const WEEKDAY_SHORT = Object.freeze(["lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim."]);
export const WEEKDAY_LONG = Object.freeze([
  "lundi",
  "mardi",
  "mercredi",
  "jeudi",
  "vendredi",
  "samedi",
  "dimanche",
]);

/** « samedi 27 septembre 2026 » — the accessible name of a day cell. */
export function longDateLabel(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return `${WEEKDAY_LONG[weekdayIndex(iso)]} ${d} ${MONTH_NAMES[m - 1]} ${y}`;
}
